import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePutRequest } from '../../service/PutTemplate';

const panamaDate = (value) => {
    if (!value) return 'No disponible';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'No disponible';
    return new Intl.DateTimeFormat('es-PA', {
        timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(date);
};
const statusTag = (status) => {
    const labels = { success: 'Exitosa', failed: 'Fallida', unknown: 'Desconocida' };
    const severity = status === 'success' ? 'success' : status === 'failed' ? 'danger' : 'warning';
    return <Tag severity={severity} value={labels[status] || status || 'Desconocida'} />;
};
const supplyLabel = (row) => row.status === 'not_configured_provisional'
    ? <Tag severity="warning" value="No configurado — provisional" />
    : <span>{row.rawBalance ?? '—'}</span>;
const Metric = ({ label, value, tone = 'primary', icon, hint }) => (
    <Card className={`jetinno-metric jetinno-metric--${tone}`}>
        <div className="jetinno-metric__icon"><i className={icon} /></div>
        <div><span>{label}</span><strong>{value ?? '—'}</strong>{hint ? <small>{hint}</small> : null}</div>
    </Card>
);
const panamaDateKey = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(date);
const defaultFromDate = () => panamaDateKey(new Date(Date.now() - (6 * 24 * 60 * 60 * 1000)));
const initialPagination = () => ({
    devices: { page: 0, rows: 25, total: 0 }, orders: { page: 0, rows: 25, total: 0 },
    alerts: { page: 0, rows: 25, total: 0 }, supplies: { page: 0, rows: 25, total: 0 }
});

const JetinnoDirectTelemetry = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [machineNo, setMachineNo] = useState('');
    const [from, setFrom] = useState(defaultFromDate);
    const [to, setTo] = useState(panamaDateKey);
    const [orderStatus, setOrderStatus] = useState('');
    const [orderProduct, setOrderProduct] = useState('');
    const [orderPayType, setOrderPayType] = useState('');
    const [summary, setSummary] = useState(null);
    const [devices, setDevices] = useState([]);
    const [orders, setOrders] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [supplies, setSupplies] = useState([]);
    const [authorizedMachines, setAuthorizedMachines] = useState([]);
    const [newMachineNo, setNewMachineNo] = useState('');
    const [pagination, setPagination] = useState(initialPagination);
    const [orderDetail, setOrderDetail] = useState(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);
    const initialLoadRef = useRef(false);
    const login = JSON.parse(localStorage.getItem('adminUser') || localStorage.getItem('userDetails') || '{}');
    const roleUid = login?.data?.adminUser?.role?.uid;
    const isAdmin = roleUid === 'admin' || roleUid === 'superAdmin';

    const eventRangeParams = useCallback((params) => {
        if (from) params.set('from', new Date(`${from}T00:00:00-05:00`).toISOString());
        if (to) params.set('to', new Date(`${to}T23:59:59.999-05:00`).toISOString());
        return params;
    }, [from, to]);
    const buildQuery = useCallback((type, pageState = pagination[type]) => {
        const params = new URLSearchParams({ page: String(pageState.page + 1), limit: String(pageState.rows) });
        if (machineNo.trim()) params.set('machineNo', machineNo.trim());
        if (type !== 'devices') eventRangeParams(params);
        if (type === 'orders') {
            if (orderStatus) params.set('status', orderStatus);
            if (orderProduct.trim()) params.set('product', orderProduct.trim());
            if (orderPayType.trim()) params.set('payType', orderPayType.trim());
        }
        return params.toString();
    }, [eventRangeParams, machineNo, orderPayType, orderProduct, orderStatus, pagination]);

    const collectionEndpoint = useCallback((type, query) => ({
        devices: endPoints.jetinnoTelemetryDevices,
        orders: endPoints.jetinnoTelemetryOrders,
        alerts: endPoints.jetinnoTelemetryAlerts,
        supplies: endPoints.jetinnoTelemetrySupplies
    })[type](query), []);

    const applyCollectionResponse = useCallback((type, response, pageState) => {
        const setters = { devices: setDevices, orders: setOrders, alerts: setAlerts, supplies: setSupplies };
        setters[type](response?.data?.items || []);
        setPagination((current) => ({
            ...current,
            [type]: { ...pageState, total: Number(response?.data?.pagination?.total || 0) }
        }));
    }, []);

    const loadCollectionPage = useCallback(async (type, pageState) => {
        setLoading(true);
        try {
            const response = await handleGetRequest(collectionEndpoint(type, buildQuery(type, pageState)), false, false, dispatch);
            applyCollectionResponse(type, response, pageState);
        } finally {
            setLoading(false);
        }
    }, [applyCollectionResponse, buildQuery, collectionEndpoint, dispatch]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const summaryParams = eventRangeParams(new URLSearchParams());
            if (machineNo.trim()) summaryParams.set('machineNo', machineNo.trim());
            if (orderStatus) summaryParams.set('status', orderStatus);
            if (orderProduct.trim()) summaryParams.set('product', orderProduct.trim());
            if (orderPayType.trim()) summaryParams.set('payType', orderPayType.trim());
            const reset = Object.fromEntries(Object.entries(pagination).map(([key, value]) => [key, { ...value, page: 0 }]));
            const [summaryResponse, devicesResponse, ordersResponse, alertsResponse, suppliesResponse] = await Promise.all([
                handleGetRequest(endPoints.jetinnoTelemetrySummary(summaryParams.toString()), false, false, dispatch),
                handleGetRequest(collectionEndpoint('devices', buildQuery('devices', reset.devices)), false, false, dispatch),
                handleGetRequest(collectionEndpoint('orders', buildQuery('orders', reset.orders)), false, false, dispatch),
                handleGetRequest(collectionEndpoint('alerts', buildQuery('alerts', reset.alerts)), false, false, dispatch),
                handleGetRequest(collectionEndpoint('supplies', buildQuery('supplies', reset.supplies)), false, false, dispatch)
            ]);
            setSummary(summaryResponse?.data || null);
            applyCollectionResponse('devices', devicesResponse, reset.devices);
            applyCollectionResponse('orders', ordersResponse, reset.orders);
            applyCollectionResponse('alerts', alertsResponse, reset.alerts);
            applyCollectionResponse('supplies', suppliesResponse, reset.supplies);
            if (isAdmin) {
                const authorizedResponse = await handleGetRequest(endPoints.jetinnoAuthorizedMachines, false, false, dispatch);
                setAuthorizedMachines(authorizedResponse?.data || []);
            }
        } finally {
            setLoading(false);
        }
    }, [applyCollectionResponse, buildQuery, collectionEndpoint, dispatch, eventRangeParams, isAdmin, machineNo, orderPayType, orderProduct, orderStatus, pagination]);

    const showOrderDetail = async (row) => {
        setOrderDetailLoading(true);
        setOrderDetail({ visible: true, data: row });
        try {
            const response = await handleGetRequest(endPoints.jetinnoTelemetryOrder(row.machineNo, row.orderNo), false, false, dispatch);
            setOrderDetail({ visible: true, data: response?.data || row });
        } finally {
            setOrderDetailLoading(false);
        }
    };

    const setAuthorization = async (targetMachineNo, enabled) => {
        const normalized = String(targetMachineNo || '').trim();
        if (!normalized) return;
        await dispatch(handlePutRequest({ enabled }, endPoints.jetinnoAuthorizedMachine(normalized), false, true));
        setNewMachineNo('');
        refresh();
    };

    useEffect(() => {
        if (initialLoadRef.current) return;
        initialLoadRef.current = true;
        refresh();
    }, [refresh]);

    const lazyTableProps = (type) => ({
        lazy: true,
        paginator: true,
        first: pagination[type].page * pagination[type].rows,
        rows: pagination[type].rows,
        totalRecords: pagination[type].total,
        rowsPerPageOptions: [15, 25, 50, 100],
        onPage: (event) => loadCollectionPage(type, {
            page: event.page,
            rows: event.rows,
            total: pagination[type].total
        })
    });
    const linkedMachine = (row) => row.syncroMachine
        ? <Tag severity="success" value={`Vinculada · ${row.syncroMachine.number}`} />
        : <Tag severity="warning" value="Sin vincular" />;
    const routeValue = (row) => row.syncroMachine?.route || '—';
    const detailData = orderDetail?.data;
    const onlineRate = summary?.devices?.total
        ? Math.round((summary.devices.online / summary.devices.total) * 100)
        : 0;
    const successRate = summary?.orders?.total
        ? Math.round((summary.orders.successful / summary.orders.total) * 100)
        : 0;

    return (
        <div className="jetinno-direct">
            <div className="jetinno-header">
                <div><span className="jetinno-eyebrow">Fuente: servidor Amevel · puerto 4000</span><h1>Telemetría directa</h1><p>Hora mostrada en Panamá; recepción UTC es la referencia autoritativa.</p></div>
                <div className="flex flex-wrap gap-2">
                    <Button label="Consumo por máquina" icon="pi pi-table" className="p-button-outlined" onClick={() => navigate('/jetinno/consumption')} />
                    <Button label="Actualizar" icon="pi pi-refresh" loading={loading} onClick={refresh} />
                </div>
            </div>
            <Card title="Filtros del dashboard" className="jetinno-filter-card">
                <p className="jetinno-filter-help">El período aplica a órdenes, errores y suministros. Producto, resultado y pago actualizan todas las métricas y análisis de órdenes; el estado de las máquinas siempre muestra el contacto más reciente.</p>
                <div className="jetinno-filter-grid">
                    <label><span>Desde</span><InputText type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} /></label>
                    <label><span>Hasta</span><InputText type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} /></label>
                    <label><span>Máquina Jetinno</span><InputText value={machineNo} onChange={(event) => setMachineNo(event.target.value)} placeholder="Ej. 90037" /></label>
                    <label><span>Producto</span><InputText value={orderProduct} onChange={(event) => setOrderProduct(event.target.value)} placeholder="Nombre del producto" /></label>
                    <label><span>Resultado de orden</span><Dropdown value={orderStatus} onChange={(event) => setOrderStatus(event.value)} options={[
                        { label: 'Todos', value: '' }, { label: 'Exitosa', value: 'success' },
                        { label: 'Fallida', value: 'failed' }, { label: 'Desconocida', value: 'unknown' }
                    ]} /></label>
                    <label><span>Tipo de pago</span><InputText value={orderPayType} onChange={(event) => setOrderPayType(event.target.value)} placeholder="Ej. free" /></label>
                    <Button label="Aplicar filtros" icon="pi pi-filter" onClick={refresh} />
                </div>
            </Card>
            <div className="jetinno-provisional"><i className="pi pi-info-circle" /><span>9999 = No configurado — provisional · Ventas monetarias no verificadas · Zona horaria interna no confirmada · Cancelaciones y reembolsos no reportados.</span></div>
            <div className="jetinno-section-heading"><div><span>Resumen operativo</span><h2>Estado general</h2></div><small>Último dato: {panamaDate(summary?.latestContactAt)}</small></div>
            <div className="jetinno-metrics jetinno-metrics--direct">
                <Metric label="Máquinas" value={summary?.devices?.total} icon="pi pi-desktop" hint="Registradas en telemetría" />
                <Metric label="En línea" value={summary?.devices?.online} tone="success" icon="pi pi-wifi" hint={`${onlineRate}% conectadas`} />
                <Metric label="Fuera de línea" value={summary?.devices?.offline} tone="neutral" icon="pi pi-ban" hint="Sin contacto reciente" />
                <Metric label="Órdenes" value={summary?.orders?.total} tone="info" icon="pi pi-shopping-cart" hint={`${successRate}% exitosas`} />
                <Metric label="Fallidas" value={summary?.orders?.failed} tone="danger" icon="pi pi-times-circle" hint="Requieren revisión" />
                <Metric label="Alertas" value={summary?.alerts?.total} tone="warning" icon="pi pi-exclamation-triangle" hint="En el período" />
            </div>
            <div className="jetinno-section-heading"><div><span>Comportamiento</span><h2>Actividad del período</h2></div><small>{from || 'Inicio'} — {to || 'Hoy'}</small></div>
            <div className="jetinno-report-grid">
                <Card title="Órdenes por hora" className="jetinno-table-card">
                    <DataTable value={summary?.ordersByHour || []} emptyMessage="Sin órdenes en el período" scrollable scrollHeight="18rem">
                        <Column field="hour" header="Hora (Panamá)" body={(row) => panamaDate(row.hour)} />
                        <Column field="total" header="Total" /><Column field="successful" header="Exitosas" /><Column field="failed" header="Fallidas" />
                    </DataTable>
                </Card>
                <Card title="Órdenes por producto" className="jetinno-table-card">
                    <DataTable value={summary?.topProducts || []} emptyMessage="Sin productos en el período" scrollable scrollHeight="18rem">
                        <Column field="productId" header="ID" /><Column field="productName" header="Producto" /><Column field="total" header="Órdenes" />
                    </DataTable>
                </Card>
            </div>
            <div className="jetinno-section-heading"><div><span>Investigación</span><h2>Detalle de telemetría</h2></div><small>Consulta paginada desde MongoDB</small></div>
            <Card className="jetinno-table-card">
                <TabView>
                    <TabPanel header="Máquinas">
                        <DataTable value={devices} loading={loading} responsiveLayout="scroll" emptyMessage="Sin telemetría directa" {...lazyTableProps('devices')}>
                            <Column field="machineNo" header="Máquina" />
                            <Column header="Vínculo Syncro" body={linkedMachine} />
                            <Column header="Ruta" body={routeValue} />
                            <Column field="displayLocation" header="Ubicación" body={(row) => row.displayLocation || '—'} />
                            <Column header="Estado" body={(row) => <Tag severity={row.online ? 'success' : 'danger'} value={row.online ? 'En línea' : 'Fuera de línea'} />} />
                            <Column field="model" header="Modelo" />
                            <Column field="appVersion" header="Versión" />
                            <Column field="lastSeenAt" header="Último contacto" body={(row) => panamaDate(row.lastSeenAt)} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Órdenes">
                        <DataTable value={orders} loading={loading} responsiveLayout="scroll" emptyMessage="Sin órdenes recibidas" {...lazyTableProps('orders')}>
                            <Column field="receivedAt" header="Recibida" body={(row) => panamaDate(row.receivedAt)} />
                            <Column field="machineNo" header="Máquina" /><Column header="Ruta" body={routeValue} /><Column field="orderNo" header="Orden" /><Column field="productName" header="Producto" />
                            <Column field="payType" header="Pago" body={(row) => row.payType || 'No informado'} />
                            <Column header="Resultado" body={(row) => statusTag(row.status)} />
                            <Column header="Materiales" body={(row) => row.materialConsumptionStatus === 'reported' ? 'Reportados' : 'No informado'} />
                            <Column header="Monto" body={() => 'No verificado'} />
                            <Column header="Detalle" body={(row) => <Button icon="pi pi-eye" className="p-button-rounded p-button-text" tooltip="Ver transformación" onClick={() => showOrderDetail(row)} />} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Errores y advertencias">
                        <DataTable value={alerts} loading={loading} responsiveLayout="scroll" emptyMessage="Sin errores ni advertencias" {...lazyTableProps('alerts')}>
                            <Column field="receivedAt" header="Recibido" body={(row) => panamaDate(row.receivedAt)} /><Column field="machineNo" header="Máquina" /><Column header="Ruta" body={routeValue} /><Column field="code" header="Código" /><Column field="title" header="Descripción" /><Column field="solution" header="Solución" body={(row) => row.solution || 'No disponible'} /><Column field="catalogMatch" header="Catálogo" />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Suministros">
                        <DataTable value={supplies} loading={loading} responsiveLayout="scroll" emptyMessage="Sin suministros reportados" {...lazyTableProps('supplies')}>
                            <Column field="receivedAt" header="Recibido" body={(row) => panamaDate(row.receivedAt)} /><Column field="machineNo" header="Máquina" /><Column header="Ruta" body={routeValue} /><Column field="code" header="Código" /><Column field="label" header="Suministro" /><Column header="Valor" body={supplyLabel} />
                        </DataTable>
                    </TabPanel>
                </TabView>
            </Card>
            {isAdmin ? (
                <Card title="Máquinas autorizadas" className="jetinno-table-card jetinno-authorizations">
                    <p>Solo las máquinas habilitadas reciben respuestas y mantienen una sesión en el puerto 4000.</p>
                    <div className="jetinno-toolbar"><InputText value={newMachineNo} onChange={(event) => setNewMachineNo(event.target.value)} placeholder="Número de máquina" /><Button label="Autorizar" icon="pi pi-plus" onClick={() => setAuthorization(newMachineNo, true)} /></div>
                    <DataTable value={authorizedMachines} emptyMessage="No hay autorizaciones guardadas en MongoDB">
                        <Column field="machineNo" header="Máquina" /><Column header="Estado" body={(row) => <Tag severity={row.enabled ? 'success' : 'danger'} value={row.enabled ? 'Autorizada' : 'Deshabilitada'} />} />
                        <Column body={(row) => <Button label={row.enabled ? 'Deshabilitar' : 'Habilitar'} className="p-button-text" onClick={() => setAuthorization(row.machineNo, !row.enabled)} />} />
                    </DataTable>
                </Card>
            ) : null}
            <Dialog visible={Boolean(orderDetail?.visible)} header={`Detalle de orden ${detailData?.orderNo || ''}`} modal style={{ width: 'min(920px, 95vw)' }} onHide={() => setOrderDetail(null)}>
                {orderDetailLoading ? <div className="jetinno-detail-loading"><i className="pi pi-spin pi-spinner" /> Cargando detalle…</div> : detailData ? (
                    <div className="jetinno-order-detail">
                        <div className="jetinno-detail-grid">
                            <div><span>Máquina Jetinno</span><strong>{detailData.machineNo || '—'}</strong></div>
                            <div><span>Máquina Syncro</span><strong>{detailData.syncroMachine?.number || 'Sin vincular'}</strong></div>
                            <div><span>Ruta</span><strong>{routeValue(detailData)}</strong></div>
                            <div><span>Ubicación</span><strong>{detailData.displayLocation || '—'}</strong></div>
                            <div><span>Producto</span><strong>{detailData.productName || '—'} {detailData.productId ? `(#${detailData.productId})` : ''}</strong></div>
                            <div><span>Resultado</span>{statusTag(detailData.status)}</div>
                            <div><span>Pago</span><strong>{detailData.payType || 'No informado'}</strong></div>
                            <div><span>Recibida</span><strong>{panamaDate(detailData.receivedAt)}</strong></div>
                            <div><span>Reenvíos</span><strong>{detailData.resendCount ?? 0}</strong></div>
                            <div><span>Motivo de falla</span><strong>{detailData.failReason || 'No informado'}</strong></div>
                        </div>
                        {isAdmin && detailData.originalEvent ? <div className="jetinno-raw-event"><h3>Evento original recibido</h3><p>Vista técnica del mensaje decodificado antes de transformarlo en orden.</p><pre>{JSON.stringify(detailData.originalEvent, null, 2)}</pre></div> : null}
                    </div>
                ) : <p>No se encontró el detalle de la orden.</p>}
            </Dialog>
        </div>
    );
};

export default JetinnoDirectTelemetry;
